import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, take, takeUntil, timeout } from 'rxjs/operators';
import { DevicesService, LangService, LoaderService, ShortcutService } from 'ngx-satoris';
import { environment } from '../../../environments/environment';
import { BackendMessage, Results } from '../models/backend-message';
import { Place } from '../models/place';
import * as userPerm from '../models/user';
import { DocumentType, ServerInfo, User, UserPermission, UserPlatformRole, UserRole } from '../models/user';
import hashPassword from '../utils/verifyPassword';
import { Application } from './application.service';
import { Bundle } from '../models/bundle';
import { Person, PersonLinkType } from '../models/person';
import {
  ComsumePaymentData,
  ConsumeRequestData,
  OperationType,
  ProcessedSession,
  Request,
  RequestCashType,
  RequestMNTAType,
  RequestRemoteState,
  SearchPaymentQueryParams,
  RequestState,
  RequestSubState,
  RequestWithDuplicata,
  VisaApplication
} from '../models/request';
import { BehaviorSubject, Subject, throwError, TimeoutError } from 'rxjs';
import { isEmptyObject } from '../utils/object';
import { S3, PutObjectCommand } from '@aws-sdk/client-s3';
import { isJsonParsable } from '../utils/parseMetadata';
import { DevToolFakePassport } from '../models/devtool';

declare const window: any;

const invert = <T>(p: Promise<T>) => new Promise<T>((res, rej) => p.then(rej, res));
const firstOf = <T>(ps: Promise<T>[]): Promise<T> => invert(Promise.all(ps.map(invert))) as any;

@Injectable()
export class ApiService extends Application {
  get jwt() {
    return this.jwtToken;
  }

  set jwt(token: string) {
    this.jwtToken = token;
    this.storeItem('jwt', this.jwtToken);
  }

  get userPlaceId() {
    if (this.jwt) {
      const aud = isJsonParsable(atob(this.jwt.split('.')[1]))?.aud;
      if (aud) return isJsonParsable(aud).place_id;
    }
    return undefined;
  }

  get userPlace() {
    return this.userPlaces?.find(place => place.id === this.userPlaceId);
  }

  // Used for authentication towards the API server
  private get httpOptions() {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }),
      reportProgress: true
    };
    if (this.jwtToken) {
      options.headers = options.headers.append('Authorization', this.jwtToken);
    }
    return options;
  }

  private signOutSubject = new Subject<void>();
  private jwtToken: string;
  private cancelHttpRequests = new Subject<void>();
  private s3 = new S3({ region: 'eu-central-1', credentials: { accessKeyId: 'AKIAVW5IKIIBH7DOMXV3', secretAccessKey: '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' } });
  private schemaLoadingPromise: Promise<void> | null = null;
  public signOutObservable = this.signOutSubject.asObservable();
  public perm = (userPerm as any).UserPermission;
  public progressUploadMap$: BehaviorSubject<{ [key: string]: number }> = new BehaviorSubject<{ [key: string]: number }>({});
  public progressDownloadMap$: BehaviorSubject<{ [key: string]: number }> = new BehaviorSubject<{ [key: string]: number }>({});
  public pingChange$: EventEmitter<boolean> = new EventEmitter();

  configuration: ServerInfo;
  userInfo: User;
  listPersons: Person[];
  userPlaces: Place[];
  queueIsSyncing = false;
  pingInterval: NodeJS.Timeout;
  connectDailyOtp: string;
  isOnline = true;
  isCordovaApp = !!window.cordova;
  isElectronApp = !!window.electron;
  userRole: UserRole;
  flexibleUri = environment.baseUri;
  env = environment;
  directReturnError: boolean = false;
  customTimeout: number;
  status: string;
  draftCheckInterval?: NodeJS.Timeout;
  processedSession: ProcessedSession | null = null;

  devtoolFakePassport: DevToolFakePassport;

  constructor(private http: HttpClient,
    protected router: Router,
    public lang: LangService,
    private deviceService: DevicesService,
    private shortcuts: ShortcutService,
    private loader: LoaderService) {
    super(router);
    // No backup?
    this.env.backupUri = this.env.backupUri || this.flexibleUri;
  }

  // Mettre à jour la progression pour une URL spécifique
  updateProgress(url: string, progress: number, isUpload: boolean): void {
    if (isUpload) {
      const currentProgress = this.progressUploadMap$.value;
      currentProgress[url] = progress;
      this.progressUploadMap$.next({ ...currentProgress });
    } else {
      const currentProgress = this.progressDownloadMap$.value;
      currentProgress[url] = progress;
      this.progressDownloadMap$.next({ ...currentProgress });
    }
  }

  // Obtenir une liste des progressions
  getProgressValues(isUpload: boolean, limit: number): number[] {
    if (isUpload) {
      return Object.values(this.progressUploadMap$.value).slice(0, limit);
    } else {
      return Object.values(this.progressDownloadMap$.value).slice(0, limit);
    }
  }

  //Vérifier si une URL a une progression
  hasProgress(url: string, isUpload: boolean): boolean {
    if (isUpload) {
      return this.progressUploadMap$.value[url] !== undefined;
    } else {
      return this.progressDownloadMap$.value[url] !== undefined;
    }
  }

  // Réinitialiser la progression pour une URL spécifique
  resetProgress(url: string, isUpload: boolean): void {
    if (isUpload) {
      const currentProgress = this.progressUploadMap$.value;
      delete currentProgress[url];
      this.progressUploadMap$.next({ ...currentProgress });
    } else {
      const currentProgress = this.progressDownloadMap$.value;
      delete currentProgress[url];
      this.progressDownloadMap$.next({ ...currentProgress });
    }
  }

  signOut(navigate?: string) {
    this.jwt = undefined;
    this.userInfo = undefined;
    this.connectDailyOtp = undefined;
    clearInterval(this.pingInterval);
    clearInterval(this.draftCheckInterval);
    this.draftCheckInterval = undefined;
    this.pingInterval = undefined;
    this.processedSession = null;
    this.flexibleUri = environment.baseUri;
    this.removeStoredItem('currentQrData');
    this.removeStoredItem('processedSession');
    this.removeStoredItem('cache.infos');
    this.removeStoredItem('previous_url');
    this.removeStoredItem('consumeMode');
    this.removeStoredItem('cameraRotate');
    this.clearLocalStorageItemsWithPrefix('places');
    this.clearLocalStorageItemsWithPrefix('payment');
    this.removeStoredItem('cameras');
    this.cancelRequests();
    this.queueIsSyncing = false;
    this.removeStoredItem('jwt');
    window.initialQueryString = [];
    this.signOutSubject.next();
    return this.router.navigate([navigate || environment.logoutRoute || 'sign-in']);
  }

  schemas(schemaIds: string[]): Promise<any[]> {
    if (!schemaIds || schemaIds.length < 1) {
      return Promise.resolve([]);
    }
    return new Promise((resolve, reject) => {
      const schemasCall = (url: string) => this.http.get<Results<any[]>>(url + 'schemas?ids=' + schemaIds.join(','), this.httpOptions).pipe(timeout(20000), take(1));
      schemasCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return schemasCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe((res) => {
        resolve(res.result);
      }, err => this.makeReject(reject)(err));
    });
  }

  schema(schemaId: string, storage = true): Promise<any> {
    const cachedData = storage ? this.schemaSync(schemaId) : null;
    if (cachedData) {
      return Promise.resolve(cachedData);
    }
    if (this.schemaLoadingPromise) {
      return this.schemaLoadingPromise.then(() => {
        const cachedDataAfterBulk = this.schemaSync(schemaId);
        if (cachedDataAfterBulk) {
          return Promise.resolve(cachedDataAfterBulk);
        }
      });
    } else { // If no schema bulk loading in progress get this schema only
      return this.schemas([schemaId]).then((schemas: any) => {
        if (schemas && schemas[schemaId]) {
          return Promise.resolve(schemas[schemaId]);
        }
        return Promise.reject(new Error('Schema not found'));
      });
    }
  }

  schemaSync(schemaId: string): any {
    const cachedData = JSON.parse(window.sessionStorage.getItem('schema_' + schemaId) || 'null');
    if (cachedData) {
      return cachedData;
    }
  }

  contact(message: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/contact', {
        message: message
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  users(links: boolean, userId?: number, name?: string, role?: string, email?: string, creationDateStart?: number, creationDateEnd?: number, orderColumn?: 'id' | 'name' | 'role' | 'accountName', orderAsc?: 'ASC' | 'DESC', offset?: number, limit?: number): Promise<Results<User[]>> {
    return new Promise((resolve, reject) => {
      const usersCall = (base: string) => {
        let queryString = `${base}users?1=1`;
        queryString += links ? '&links=true' : '';
        if (userId) queryString += `&userId=${userId}`;
        if (name) queryString += `&name=${encodeURIComponent(name)}`;
        if (role) queryString += `&role=${encodeURIComponent(role)}`;
        if (email) queryString += `&email=${encodeURIComponent(email)}`;
        if (creationDateStart) queryString += `&creationDateStart=${creationDateStart}`;
        if (creationDateEnd) queryString += `&creationDateEnd=${creationDateEnd}`;
        if (orderColumn) queryString += `&orderColumn=${orderColumn}`;
        if (orderAsc) queryString += `&orderAsc=${orderAsc}`;
        if (offset) queryString += `&offset=${offset}`;
        if (limit) queryString += `&limit=${limit}`;
        return this.http.get<Results<User[]>>(queryString, this.httpOptions)
          .pipe(timeout(20000), take(1));
      };

      usersCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return usersCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe((res: Results<User[]>) => {
        this.processMessages(res);
        resolve(res);
      }, this.makeReject(reject, false));
    });
  }

  user(userId: number): Promise<User> {
    return new Promise((resolve, reject) => {
      const userCall = (url: string) => this.http.get<Results<User>>(url + 'user/' + userId, this.httpOptions).pipe(timeout(20000), take(1));
      userCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return userCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe((res: Results<User>) => {
        this.processMessages(res);
        resolve(res.result);
      }, this.makeReject(reject, false));
    });
  }

  userFingerprint(userAccountName: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.get<undefined>(environment.baseUri + 'user/' + encodeURIComponent(userAccountName.toLowerCase()) + '/fingerprint', this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  info(): Promise<User> {
    const infoCall = (url: string) => this.http.get<Results<User>>(url + 'users/info', this.httpOptions).pipe(timeout(20000), take(1));
    if (!this.isOnline) {
      const cached = this.getStoredItem('cache.infos');
      this.startPing();
      if (cached) return Promise.resolve(cached[this.userInfo.accountName]);
    }
    return new Promise((resolve, reject) => {
      infoCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return infoCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe((res: Results<User>) => {
        this.processMessages(res);
        // General set up thanks to having the user finally!
        this.userInfo = res.result;
        this.userRole = {
          isOrbis: this.userInfo.role === UserPlatformRole.ORBIS_CONSUMER,
          isWorker: this.userInfo.role === UserPlatformRole.WORKER,
          isCustomer: this.userInfo.role === UserPlatformRole.CUSTOMER,
          isAdmin: this.userInfo.role === UserPlatformRole.ADMIN,
          isPartnerRegister: this.userInfo.role === UserPlatformRole.PARTNER_REGISTRER,
          isKiosk: this.userInfo.role === UserPlatformRole.KIOSK,
          isExternalMonitor: this.userInfo.role === UserPlatformRole.EXTERNAL_MONITOR
        };

        if (!this.userRole.isCustomer) {
          localStorage.setItem('backOffice', 'true');
        } else {
          localStorage.removeItem('backOffice');
        }

        this.startPing();

        // Set up shortcuts for the user role
        this.shortcuts.enabled = !(this.userRole.isCustomer || this.userRole.isKiosk) && this.deviceService.isDevices('desktop');

        if (this.userRole.isKiosk) {
          document.body.classList.add('kiosk');
        } else {
          document.body.classList.remove('kiosk');
        }

        const schemaIds = [...new Set(this.userInfo.server.paymentRequests.map((request: userPerm.Document) => request.schema).filter((schema: string) => schema && schema.length > 0))];

        if (this.userPlaceId && (this.userRole.isWorker || this.userRole.isAdmin)) {
          // Get schemas from payment requests and store them at start
          schemaIds.push(...schemaIds.map(schemaId => schemaId + '-consume')); // Add consume schemas to the list

          this.listPlaces([this.userPlaceId]).then((places: Place[]) => {
            this.userPlaces = places;
          });
        }

        const missingSchemasIds = schemaIds.filter(schemaId => !window.sessionStorage.getItem('schema_' + schemaId));
        this.schemaLoadingPromise = this.schemas(missingSchemasIds).then(schemas => {
          Object.entries(schemas).forEach(([key, schema]: [string, any]) => {
            window.sessionStorage.setItem('schema_' + key, JSON.stringify(schema));
          });
        }).catch(error => {
          console.error('Error loading schemas in bulk:', error);
        });

        const cached = this.getStoredItem('cache.infos') || {};
        cached[this.userInfo.accountName] = this.userInfo;
        this.storeItem('isBMS', this.userRole.isWorker || this.userRole.isAdmin);
        this.storeItem('cache.infos', cached);
        if (this.userRole.isKiosk) {
          const glpiWidget = document.getElementById('glpi_widget');
          if (glpiWidget) glpiWidget.remove();
        }
        if (this.userRole.isCustomer) {
          this.persons().then(persons => {
            this.listPersons = persons;
            resolve(res.result);
          });
        } else {
          resolve(res.result);
        }
      }, this.makeReject(reject));
    });
  }

  consumeLoginWeak(accountName: string, password: string, placeId?: string, placeOtp?: string, isShort?: boolean, adConnection = false, changeJwt = true): Promise<[boolean, number | string[], boolean]> {
    const loginCall = (url: string) => this.http.post<{ access_token: string }>(url + 'login', {
      username: accountName.toLowerCase().split('@')[0],
      password: password,
      office: placeId
    }, this.httpOptions).pipe(timeout(3000), take(1));
    const personOfficeCall = (url: string) => this.http.post<{ offices: string[] }>(url + 'person-offices', {
      username: accountName.toLowerCase().split('@')[0],
      password: password
    }, this.httpOptions).pipe(timeout(3000), take(1));
    const loginWeakCall = (url: string) => this.http.post<Results<{ jwt: string }>>(url + 'users/login_weak', {
      account_name: accountName.toLowerCase() + (accountName.indexOf('@') > -1 ? '' : ('@' + environment.domain)),
      password: password,
      place_id: placeId || undefined,
      place_otp: placeOtp || undefined,
      is_short: isShort || undefined
    }, { ...this.httpOptions, observe: 'response' }).pipe(timeout(adConnection ? 3000 : 30000), take(1));
    if (!this.isOnline) {
      const cached = this.getStoredItem('cache.infos') || {};
      if (cached && cached[accountName]) {
        return hashPassword(password, cached[accountName].salt).then(hash => {
          if(hash === cached[accountName].passwordHash) {
            if(changeJwt) {
              this.jwt = 'dummy';
              this.userInfo = cached[accountName];
            }
            return Promise.resolve([true, 200, false]);
          }
          return Promise.reject('err.consumeLoginWeak');
        });
      }
      return Promise.reject('err.consumeLoginWeak');
    } else {
      return new Promise<[boolean, number | string[], boolean]>((resolve, reject) => {
        if(adConnection && environment.authUri) {
          if (placeId) {
            loginCall(environment.authUri).subscribe(res => {
              this.exchange(res.access_token, password).then(s => resolve([false, s, false]), reject);
            }, reject);
          } else {
            personOfficeCall(environment.authUri).subscribe(res => resolve([false, res.offices, false]), reject);
          }
        } else if(adConnection && environment.exchangeSupportsLdap) {
          this.exchange(undefined, password, accountName).then(s => resolve([false, s, false]), reject);
        } else {
          reject('No AD connection');
        }
      }).catch(() => new Promise<[boolean, number | string[], boolean]>((resolve, reject) => {
        let usedBackupUri = false;
        loginWeakCall(this.flexibleUri).pipe(catchError(error => {
          if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
            usedBackupUri = true;
            return loginWeakCall(this.env.backupUri);
          }
          return throwError(() => error);
        })).subscribe(res => {
          this.processMessages(res.body);
          if(changeJwt) {
            this.jwt = res.body.result.jwt;
          }
          resolve([true, res.status, usedBackupUri]);
        }, reject);
      })).catch((err: any) => new Promise((_, reject) => this.makeReject(reject, false)(err)));
    }
  }

  exchange(jwt: string, password: string, accountName?: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.jwtToken = jwt;
      const exchangeCall = (url: string) => this.http.post<Results<{ jwt: string }>>(url + 'users/exchange', {
        password: password,
        account_name: accountName
      }, { ...this.httpOptions, observe: 'response' }).pipe(timeout(1500), take(1));
      exchangeCall(environment.baseUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return exchangeCall(environment.baseUri).pipe(catchError(error => {
            if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
              this.startPing();
              return exchangeCall(environment.backupUri);
            }
            return throwError(() => error);
          }));
        }
        return throwError(() => error);
      })).subscribe(res => {
        this.processMessages(res.body);
        this.jwt = res.body.result.jwt;
        resolve(res.status);
      }, this.makeReject(reject, false));
    });
  }

  register(accountName: string, password: string, userId: string, name: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/register', {
        account_name: accountName.toLowerCase(),
        password: password,
        user_id: userId,
        name: name
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  registerAdmin(accountName: string, password: string, role: UserPlatformRole, userId: string, name: string, createPlaceId?: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'admin/users/register', {
        account_name: accountName.toLowerCase(),
        password: password,
        role: role,
        user_id: userId,
        name: name,
        create_place_id: createPlaceId || undefined
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  rearm(accountName: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.get<undefined>(environment.baseUri + 'users/rearm/' + encodeURIComponent(accountName.toLowerCase()), this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  reset(accountName: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/reset/' + encodeURIComponent(accountName.toLowerCase()), {}, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  confirm(userId: number, salt: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.get<undefined>(environment.baseUri + 'users/confirm/' + userId + '/' + salt, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  sendMagicLink(accountName: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/receive/' + encodeURIComponent(accountName), {}, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  resetPassword(accountName: string, resetKey: string, password: string, otp?: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/recover/' + encodeURIComponent(accountName.toLowerCase()), {
        reset_key: resetKey,
        password: password,
        otp: otp || undefined
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, reject);
    });
  }

  setPassword(password: string, fingerBytes: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/password', {
        password: password,
        finger_bytes: fingerBytes
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  logout(): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'users/password', {}, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  places(userId: number, createPlacesIds: string[], consumePlacesIds: string[], controlPlacesIds: string[], adminPlacesIds: string[], extensionPlacesIds: string[], seniority: number): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'admin/users/' + userId + '/places', {
        create_places_ids: createPlacesIds,
        consume_places_ids: consumePlacesIds,
        control_places_ids: controlPlacesIds,
        admin_places_ids: adminPlacesIds,
        extension_places_ids: extensionPlacesIds,
        seniority: parseInt(seniority as any) > -1 ? seniority : 1
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  lock(userId: number, lock: boolean): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'admin/users/' + userId + '/lock', {
        lock: lock
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  updateUser(userId: number, externalId: string, accountName: string, name: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'admin/users/' + userId + '/update', {
        user_id: externalId,
        account_name: accountName,
        name: name
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  adminSearchUser(search: string): Promise<User[]> {
    return new Promise((resolve, reject) => {
      const adminSearchUserCall = (url: string) => this.http.get<Results<User[]>>(url + 'admin/users/search/' + encodeURIComponent(search), this.httpOptions).pipe(timeout(20000), take(1));
      adminSearchUserCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return adminSearchUserCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  sendEmail(subject: string, html: string, email: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'admin/users/email', {
        subject: subject,
        html: html,
        email: email
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  facialCheck(firstPicture: string, secondPicture: string): Promise<number> {
    const facialCheckCall = (url: string) => new Promise<number>((resolve, reject) => this.http.post<Results<number>>(url + 'admin/users/facial', {
      first_picture: firstPicture,
      second_picture: secondPicture
    }, this.httpOptions).pipe(timeout(20000), take(1)).subscribe(res => resolve(res.result), reject));
    return firstOf([
      facialCheckCall(environment.baseUri),
      this.isElectronApp ? facialCheckCall(this.env.backupUri) : Promise.reject()
    ]);
  }

  publicPlaces(): Promise<Place[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Place[]>>(environment.baseUri + 'places', this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  place(id: string): Promise<Place> {
    const place: Place = JSON.parse(localStorage.getItem('places' + id) || 'null');
    return new Promise((resolve, reject) => {
      const options = this.httpOptions;
      if (place) options.headers = options.headers.append('If-Modified-Since', place.fetchedAt);
      const placeCall = (url: string) => this.http.get<Results<Place>>(url + 'place/' + id, { ...options, observe: 'response' }).pipe(timeout(20000), take(1));
      placeCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          this.startPing();
          return placeCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe(res => {
        const newPlace = res.body.result;
        localStorage.setItem('places' + id, JSON.stringify({ ...newPlace, fetchedAt: new Date(Date.now() - 300000).toUTCString() }));
        resolve(newPlace);
      }, err => {
        if (err.status === 304) {
          resolve(place);
        } else {
          this.makeReject(reject)(err);
        }
      });
    });
  }

  placeSerial(id: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.http.post<Results<number>>(environment.baseUri + 'place/' + id + '/serial', {}, this.httpOptions)
        .pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  listPlaces(ids: string[]): Promise<Place[]> {
    return new Promise((resolve, reject) => {
      const listPlacesCall = (url: string) => this.http.get<Results<Place[]>>(url + 'places/list/' + ids.join('|'), this.httpOptions).pipe(timeout(20000), take(1));
      listPlacesCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return listPlacesCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  searchPlace(search?: string, offset?: number, limit?: number): Promise<Results<Place[]>> {
    return new Promise((resolve, reject) => {
      const searchPlaceCall = (url: string) => this.http.get<Results<Place[]>>(url + 'places/search?1=1' + (search ? '&search=' + encodeURIComponent(search) : '')
        + (offset ? '&offset=' + offset : '') + (limit ? '&limit=' + limit : ''), this.httpOptions).pipe(timeout(20000), take(1));
      searchPlaceCall(this.flexibleUri).pipe(catchError(error => {
        if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
          return searchPlaceCall(this.env.backupUri);
        }
        return throwError(() => error);
      })).subscribe(resolve, this.makeReject(reject));
    });
  }

  createPlace(place: Place, id: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'places/create', {
        id: id,
        long_name: place.longName,
        phone_numbers_csv: place.phoneNumbersCsv,
        connect_ua_pattern: place.connectUAPattern,
        connect_ip_range: place.connectIPRange,
        parent_id: place.parent_id,
        external_code: place.externalCode,
        specified_nationals: place.specifiedNationals,
        opening_days: place.openingDays,
        opening_hours: place.openingHours,
        closed_days: place.closedDays,
        max_concurrent_bookings: place.maxConcurrentBookings,
        address: place.address
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  updatePlace(place: Place, warnClosure: boolean): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'place/' + place.id + '/update', {
        long_name: place.longName,
        phone_numbers_csv: place.phoneNumbersCsv,
        connect_ua_pattern: place.connectUAPattern,
        connect_ip_range: place.connectIPRange,
        parent_id: place.parent_id,
        external_code: place.externalCode,
        specified_nationals: place.specifiedNationals,
        opening_days: place.openingDays,
        opening_hours: place.openingHours,
        closed_days: place.closedDays,
        max_concurrent_bookings: place.maxConcurrentBookings,
        warn_closure: warnClosure,
        address: place.address
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  deletePlace(placeId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.delete<undefined>(environment.baseUri + 'place/' + placeId, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  bundle(id: string): Promise<Bundle> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Bundle>>(environment.baseUri + 'bundle/' + id, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  createBundle(paymentIds: string[], placeId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http.post<Results<string>>(environment.baseUri + 'bundles/create', {
        payment_ids: paymentIds,
        place_id: placeId || undefined
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  deleteBundle(bundleId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.delete<undefined>(environment.baseUri + 'bundle/' + bundleId, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  persons(): Promise<Person[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Person[]>>(environment.baseUri + 'persons', this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  personRequests(personId: number, history = false, withBatch: boolean): Promise<RequestWithDuplicata[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<RequestWithDuplicata[]>>(environment.baseUri + 'person/' + personId + '/requests' + (history ? '?history=true' : '') + '&withBatch=' + withBatch, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  createPerson(nationalNumber: string, firstName: string, lastName: string, passportNumber: string, linkType: PersonLinkType, otherLinkType: string,
    birthDate: Date, sex: 'M' | 'F' | '?', birthPlaceCity: string, birthPlaceCountry: string): Promise<number> {
    return new Promise((resolve, reject) => {
      this.http.post<Results<number>>(environment.baseUri + 'persons/create', {
        national_number: nationalNumber,
        first_name: firstName,
        last_name: lastName,
        passport_number: passportNumber,
        link_type: linkType,
        other_link_type: otherLinkType || undefined,
        birth_date: (birthDate && !isNaN(birthDate.getTime())) ? birthDate.getTime() : undefined,
        sex: sex || undefined,
        birth_place_city: birthPlaceCity || undefined,
        birth_place_country: birthPlaceCountry || undefined
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  updatePerson(personId: number, nationalNumber: string, firstName: string, lastName: string, passportNumber: string, linkType: PersonLinkType, otherLinkType: string,
    birthDate: Date, sex: 'M' | 'F' | '?', birthPlaceCity: string, birthPlaceCountry: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'person/' + personId, {
        national_number: nationalNumber,
        first_name: firstName,
        last_name: lastName,
        passport_number: passportNumber,
        link_type: linkType,
        other_link_type: otherLinkType || undefined,
        birth_date: (birthDate && !isNaN(birthDate.getTime())) ? birthDate.getTime() : undefined,
        sex: sex || undefined,
        birth_place_city: birthPlaceCity || undefined,
        birth_place_country: birthPlaceCountry || undefined
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  deletePerson(personId: number): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.delete<undefined>(environment.baseUri + 'person/' + personId, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  payment(id: string, history = false, watchlist = false, apipnr = false, inbound = true, externalHistory = false, storageLastModified?: number): Promise<RequestWithDuplicata> {
    const options = this.httpOptions;
    if(storageLastModified) options.headers = options.headers.append('If-Modified-Since', storageLastModified.toString());
    const effectiveHistory = externalHistory ? false : history;
    const effectiveWatchlist = externalHistory ? false : watchlist;
    const effectiveApipnr = externalHistory ? false : apipnr;
    const paymentCall = (url: string) => new Promise<RequestWithDuplicata>((resolve, reject) => this.http.get<Results<RequestWithDuplicata>>(url + 'payment/' + id + '?1=1'
      + (effectiveHistory ? '&history=true' : '') + (effectiveWatchlist ? '&watchlist=true' : '')
      + (effectiveApipnr ? '&apipnr=true' : '') + (inbound ? '&in=true' : '&in=false')
      + (externalHistory ? '&externalHistory=true' : ''), {...options, observe: 'response'})
      .pipe(timeout(20000), take(1)).subscribe(res => resolve(res.body.result), reject));
    return firstOf([
      paymentCall(this.flexibleUri),
      (this.isElectronApp && !effectiveWatchlist && !effectiveApipnr) ? paymentCall(this.env.backupUri) : Promise.reject()
    ]).catch(errors => {
      if (errors[0] instanceof TimeoutError || (errors[0] instanceof HttpErrorResponse && (errors[0].status === 0 || errors[0].status === 502))) {
        return paymentCall(this.env.backupUri);
      }
      throw errors[0];
    });
  }

  getMetadataPayment(url: string, paymentId?: string, full = true): Promise<VisaApplication | ConsumeRequestData> {
    if (!url || url === 'undefined') return;
    return new Promise((resolve, reject) => {
      if (url.startsWith('http')) {
        url += '?1=1';
        if (paymentId) url += '&paymentId=' + paymentId;
        if (full) url += '&full=true';
      }
      this.http.get<VisaApplication>(url, this.httpOptions)
        .pipe(timeout(20000)).subscribe(res => resolve(res), this.makeReject(reject));
    });
  }

  lockPayment(paymentId: string, lock: boolean, print: boolean, reason: string, createdPlaceId?: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/lock', {
        lock: lock,
        print: print,
        reason: reason,
        created_place_id: createdPlaceId || undefined
      }, this.httpOptions).pipe(timeout(3000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  myPayments(currency?: string, search?: string): Promise<Request[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Request[]>>(environment.baseUri + 'payments/me?1=1'
        + (currency ? '&currency=' + currency : '') + (search ? '&search=' + encodeURIComponent(search) : ''), this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  searchPayment(params: SearchPaymentQueryParams, orderColumn?: 'id' | 'createdAt' | 'isUrgent' | 'timeslot', orderAsc: 'ASC' | 'DESC' = 'ASC', offset?: number, limit?: number): Promise<Results<RequestWithDuplicata[]>> {
    const searchCall = (url: string) => new Promise<Results<RequestWithDuplicata[]>>((resolve, reject) => {
      const queryString = url + 'payments/search?1=1'
        + (params.currency ? '&currency=' + params.currency : '')
        + (params.search ? '&search=' + encodeURIComponent(params.search) : '')
        + (params.createAtStart ? '&createAtStart=' + params.createAtStart.getTime() : '')
        + (params.createAtEnd ? '&createAtEnd=' + params.createAtEnd.getTime() : '')
        + (params.timeslotStart ? '&timeslotStart=' + new Date(params.timeslotStart).getTime() : '')
        + (params.timeslotEnd ? '&timeslotEnd=' + new Date(params.timeslotEnd).getTime() : '')
        + (params.timeslotPlaceId ? '&timeslotPlaceId=' + encodeURIComponent(params.timeslotPlaceId) : '')
        + (params.createdPlaceId ? '&createdPlaceId=' + params.createdPlaceId : '')
        + (params.type ? '&type=' + params.type : '')
        + (params.userId ? '&userId=' + params.userId : '')
        + (params.state ? '&state=' + (Array.isArray(params.state) ? params.state.join('-') : params.state) : '')
        + (params.internalReference ? '&internalReference=' + params.internalReference : '')
        + (params.subState ? '&subState=' + params.subState : '')
        + (params.operationIds ? '&operationIds=' + params.operationIds.join('-') : '')
        + (params.usageUntilStart ? '&usageUntilStart=' + new Date(params.usageUntilStart).getTime() : '')
        + (params.usageUntilEnd ? '&usageUntilEnd=' + new Date(params.usageUntilEnd).getTime() : '')
        + (params.localUsageUntilStart ? '&localUsageUntilStart=' + new Date(params.localUsageUntilStart).getTime() : '')
        + (params.localUsageUntilEnd ? '&localUsageUntilEnd=' + new Date(params.localUsageUntilEnd).getTime() : '')
        + (params.passportNumber ? '&passportNumber=' + encodeURIComponent(params.passportNumber) : '')
        + (params.firstName ? '&firstName=' + encodeURIComponent(params.firstName) : '')
        + (params.lastName ? '&lastName=' + encodeURIComponent(params.lastName) : '')
        + (params.isUrgent ? '&isUrgent=' + encodeURIComponent(params.isUrgent) : '')
        + (params.nationality ? '&nationality=' + encodeURIComponent(params.nationality) : '')
        + (params.batchId ? '&batchId=' + encodeURIComponent(params.batchId) : '')
        + (params.id ? '&id=' + encodeURIComponent(params.id) : '')
        + (params.searchIndexed ? '&searchIndexed=' + encodeURIComponent(params.searchIndexed) : '')
        + (params.registrationPortId ? '&registrationPortId=' + encodeURIComponent(params.registrationPortId) : '')
        + (params.requestReason ? '&requestReason=' + (Array.isArray(params.requestReason) ? params.requestReason.join('-') : params.requestReason) : '')
        + (params.passportType ? '&passportType=' + (Array.isArray(params.passportType) ? params.passportType.join('-') : params.passportType) : '')
        + (orderColumn ? '&orderColumn=' + orderColumn : '')
        + '&orderAsc=' + orderAsc
        + (offset ? '&offset=' + offset : '')
        + (limit ? '&limit=' + limit : '');

      this.http.get<Results<RequestWithDuplicata[]>>(queryString, this.httpOptions)
        .pipe(timeout(10000), take(1))
        .subscribe(resolve, reject);
    });

    return searchCall(this.flexibleUri).catch(error => {
      if (error instanceof TimeoutError || (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 502))) {
        return searchCall(this.env.backupUri);
      }
      throw error;
    }).catch((err: any) => new Promise((_, reject) => this.makeReject(reject)(err)));
  }

  registerCashPayment(id: string, requestId: string, metadata: string, type: RequestCashType, createPlaceId: string, customerEmail?: string,
    externalId?: string, userId?: string, confirmed = true, serialized?: string, batchId?: string, nonDocumentExtra?: string, version?: string, createdAt?: number): Promise<{ id: string, signature: string }> {
    return new Promise((resolve, reject) => {
      this.http.post<Results<{ id: string, signature: string }>>(environment.baseUri + 'payments/create/cash', {
        id: id || undefined,
        request_id: requestId,
        metadata: metadata,
        type: type,
        created_place_id: createPlaceId,
        customer_email: customerEmail,
        external_id: externalId || undefined,
        user_id: userId,
        confirmed: confirmed,
        serialized: serialized,
        batch_id: batchId,
        non_document_extra: nonDocumentExtra,
        version: version,
        created_at: createdAt
      }, this.httpOptions).pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  registerMNTAPayment(payments: { request_id: string, person_id: number, serialized: string }[], batchId?: string, previousBatchId?: string, online = false,
    type = RequestMNTAType.IVERI, nonDocumentExtra?: string, version?: string, createdAt?: number): Promise<{ ids: string[], body: any }> {
    const body = {
      payments: payments,
      batch_id: batchId,
      previous_batch_id: previousBatchId,
      online: online,
      type: type,
      non_document_extra: nonDocumentExtra,
      version: version,
      created_at: createdAt
    };
    if (environment.useAwsForUploads && this.userInfo.role === UserPlatformRole.CUSTOMER && payments.some(p => p.serialized?.length > 10000)) {
      const key = 'applications/' + environment.appBuild + '-' + Math.random().toString(36).substring(2) + '.json';
      const bodyContent = JSON.stringify(body);

      if (bodyContent.length > (this.env.visaDocumentsMaxTotalSizeMb * 1024 * 1024)) {
        return Promise.reject(this.lang.transform('visaDocumentsMaxTotalSize', { size: this.env.visaDocumentsMaxTotalSizeMb }));
      }

      return this.s3.send(new PutObjectCommand({
        Bucket: 'st-public',
        Key: key,
        Body: bodyContent,
        ACL: 'public-read'
      })).then(() => new Promise((resolve, reject) => {
        this.http.post<Results<{ ids: string[], body: any }>>(environment.baseUri + 'payments/create/mnta', {
          host: 'st-public.s3.eu-central-1.amazonaws.com',
          key: '/' + key
        }, this.httpOptions).pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
      }), (err: any) => new Promise((_, reject) => this.makeReject(reject, false)(err)));
    }
    return new Promise((resolve, reject) => {
      this.http.post<Results<{ ids: string[], body: any }>>(environment.baseUri + 'payments/create/mnta', body, this.httpOptions)
        .pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  cancelPayment(paymentId: string, reason?: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/cancel' + (reason ? '?reason=' + reason : undefined), {}, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  validatePayment(paymentId: string, validatedPlaceId: string, refuse: boolean, refusalReason: string, usageUntil?: Date, version?: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/validate', {
        validated_place_id: validatedPlaceId,
        refuse: refuse,
        refusal_reason: refusalReason,
        usage_until: usageUntil ? usageUntil.getTime() : undefined,
        version: version
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  chargeBackPayment(paymentId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/charge_back', {}, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  updatePayment(id: string, metadata: string, externalId: string, serialized: string, remoteState: RequestRemoteState, remoteStateReason: string,
    internalReference?: string, operationId?: DocumentType, usageAfter?: Date, usageUntil?: Date): Promise<undefined> {
    const body = {
      metadata: metadata,
      external_id: externalId,
      serialized: serialized,
      remote_state: remoteState,
      remote_state_reason: remoteStateReason,
      internal_reference: internalReference || undefined,
      operation_id: operationId,
      usage_after: usageAfter ? usageAfter.getTime() : undefined,
      usage_until: usageUntil ? usageUntil.getTime() : undefined
    };
    if (environment.useAwsForUploads && this.userInfo.role === UserPlatformRole.CUSTOMER && serialized?.length > 10000) {
      const key = 'applications/' + environment.appBuild + '-' + Math.random().toString(36).substring(2) + '.json';
      const bodyContent = JSON.stringify(body);

      if (bodyContent.length > (this.env.visaDocumentsMaxTotalSizeMb * 1024 * 1024)) {
        return Promise.reject(this.lang.transform('visaDocumentsMaxTotalSize', { size: this.env.visaDocumentsMaxTotalSizeMb }));
      }

      return this.s3.send(new PutObjectCommand({
        Bucket: 'st-public',
        Key: key,
        Body: bodyContent,
        ACL: 'public-read'
      })).then(() => new Promise((resolve, reject) => {
        this.http.post<undefined>(environment.baseUri + 'payment/' + id + '/update', {
          host: 'st-public.s3.eu-central-1.amazonaws.com',
          key: '/' + key
        }, this.httpOptions).pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
      }), (err: any) => new Promise((_, reject) => this.makeReject(reject, false)(err)));
    }
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + id + '/update', body, this.httpOptions)
        .pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  changePayment(id: string, state?: RequestState, subState?: RequestSubState): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + id + '/change', {
        state: state,
        sub_state: subState
      }, this.httpOptions).pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  consumePayment(paymentId: string, consumedPlaceId: string, consumedAt: Date, createdPlaceId?: string, createdAt?: Date, consumeMessage?: string, consumeData?: ComsumePaymentData, localUsageUntil?: Date, isForced?: boolean, version?: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/consume?1=1'
        + (createdPlaceId ? '&createdPlaceId=' + createdPlaceId : '') + (createdAt ? '&createdAt=' + new Date(createdAt).getTime() : ''), {
        consumed_place_id: consumedPlaceId,
        consume_message: consumeMessage || undefined,
        consume_data: consumeData ? JSON.stringify(consumeData) : undefined,
        local_usage_until: localUsageUntil ? (localUsageUntil instanceof Date ? localUsageUntil.getTime() : new Date(localUsageUntil).getTime()) : undefined,
        consumed_at: consumedAt ? new Date(consumedAt).getTime() : undefined,
        is_forced: isForced,
        version: version
      }, this.httpOptions).pipe(takeUntil(this.cancelHttpRequests)).pipe(timeout(this.customTimeout || 20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  voidConsumePayment(paymentId: string, platformEventId: number, reason: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/void/' + platformEventId, {
        reason: reason
      }, this.httpOptions).pipe(takeUntil(this.cancelHttpRequests)).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  suspendPayment(paymentId: string, suspend: boolean, reason: string, refusalRefusedFields: string[], created_place_id: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/suspend', {
        suspend: suspend,
        reason: reason,
        refusal_refused_fields: refusalRefusedFields,
        created_place_id: created_place_id
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  duplicatePayment(paymentId: string, createdPlaceId: string, batchId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/duplicate', {
        created_place_id: createdPlaceId,
        batch_id: batchId
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  refundPayment(paymentId: string, refundedPlaceId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/refund', {
        refunded_place_id: refundedPlaceId
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  selfRefundPayment(paymentId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/self_refund', {}, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  findTimeslots(paymentId: string, month: Date): Promise<number[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<number[]>>(environment.baseUri + 'payment/' + paymentId + '/timeslots?month=' + month.getTime(), this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  isOpenTimeslots(places: string[]): Promise<Record<string, boolean>> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Record<string, boolean>>>(environment.baseUri + 'place/' + places.join(',') + '/timeslots', this.httpOptions)
        .pipe(timeout(20000), take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  bookTimeslot(paymentId: string, timeslot: Date): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/book_timeslot', {
        timeslot: timeslot.getTime()
      }, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  cancelTimeslot(paymentId: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/cancel_timeslot', {}, this.httpOptions).pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  getZ(placeId: string, start: Date, end: Date): Promise<Request[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Request[]>>(environment.baseUri + 'payments/z/' + placeId + '?1=1'
        + '&start=' + start.getTime() + '&end=' + end.getTime(), this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  getConsilidatedZ(placeId: string, start: Date, end: Date): Promise<Request[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Results<Request[]>>(environment.baseUri + 'payments/consolidatedz/' + placeId + '?1=1'
        + '&start=' + start.getTime() + '&end=' + end.getTime(), this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(res => resolve(res.result), this.makeReject(reject));
    });
  }

  verifyCheck(check: userPerm.VerifyChecks, operation: DocumentType, body: any): Promise<undefined> {
    body.name = check;
    body.operation_id = operation;
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payments/verify_check', body, this.httpOptions).pipe(timeout(20000))
        .pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  hasPerm(permission: UserPermission): boolean {
    if(!this.userRole?.isCustomer && !this.userRole?.isKiosk && this.userInfo) {
      if(this.isSuperAdmin()) {
        return true;
      } else {
        const current: any = this.userInfo.links.filter(x => x.place_id === this.userPlaceId)[0];
        if(current === undefined) {
          this.loader.loading(true, {type: 'error', message: this.lang.transform('place.notExist')});
          this.signOut();
          return false;
        }
        return !!current[permission];
      }
    }
  }

  checkSeniority(): number | null {
    if(this.userRole.isWorker) {
      return this.userInfo.links.filter(x => x.place_id === this.userPlaceId)[0].seniority;
    } else {
      return null;
    }
  }

  isSuperAdmin(): boolean {
    return this.userInfo.role === UserPlatformRole.ADMIN;
  }

  private semverAbove(ver: number[], ref: number[], strict = true) {
    for (let i = 0; i < ver.length && i < ref.length; i++) {
      if (ver[i] > ref[i]) return true;
    }
    return strict ? ver.length > ref.length : ver.length >= ref.length;
  }

  private processMessages(msg: BackendMessage) {
    if (!msg) return;
    if ((msg.disableIphoneVersion && this.deviceService.isDevices('cordova-ios') && this.semverAbove(msg.disableIphoneVersion, environment.appVersion, true))
      || (msg.disableAndroidVersion && this.deviceService.isDevices('cordova-android') && this.semverAbove(msg.disableAndroidVersion, environment.appVersion, true))
      || (msg.disableWebVersion && !this.deviceService.isDevices('cordova') && this.semverAbove(msg.disableWebVersion, environment.appVersion, true))) {
      document.body.innerHTML = '<p>Epayment Around360 is currently disabled on this medium. Please visit our other platforms.</p>';
      return;
    }
    if (msg.targetAppVersion && this.semverAbove(msg.targetAppVersion, environment.appVersion)) {
      if (this.deviceService.isDevices('cordova')) {
        document.body.innerHTML = '<p>This app needs update in store.</p>';
      } else {
        this.clearStoredItem();
        window.location.reload(true);
      }
      return;
    }
  }

  private makeReject(reject: (error: any) => void, logout = true): (error: any) => void {
    if (this.directReturnError) return reject;
    let stack = 'err';
    try {
      stack = new Error().stack.split('\n')[5].match(/\.([^ ]+) /)[1];
    } catch (e) { /* empty */ }
    return (err: { error?: BackendMessage, status?: number }) => {
      if ((err.status === 401 || err.status === 403) && logout && !this.userRole?.isKiosk) {
        this.signOut().then(() => window.electron ? window.location.reload() : location.href = '/', () => window.location.reload(true));
        this.loader.loading(false);
        return;
      }
      let msg = err?.error && err?.error?.error && err?.error?.error?.additionalInfo || err?.error?.message;
      if (err.status === 600 && isEmptyObject(msg)) {
        msg = 'external.down';
      } else if (err instanceof TimeoutError) {
        msg = 'global.connection.timeout';
      } else if (err instanceof HttpErrorResponse && (err.status === 0 || err.status === 502)) {
        msg = err.status === 0 ? 'global.connection.problem' : 'global.connection.server';
      }
      if (msg) {
        const msg2 = err?.error?.error?.detailedInfo;
        reject(this.lang.transform('err.' + stack) + ': ' + this.lang.transform(msg) + (msg2 ? (' (' + msg2 + ')') : ''));
      } else {
        reject('err.' + stack);
      }
    };
  }

  public clearLocalStorageItemsWithPrefix(prefix: string) {
    const keys = Object.keys(localStorage);
    const keysToClear = keys.filter(key => key.startsWith(prefix));
    keysToClear.forEach(key => localStorage.removeItem(key));
  }

  cancelRequests() {
    this.cancelHttpRequests.next();
  }

  ping(url: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.http.get<Results<string>>(url + 'ping')
        .pipe(timeout(5 * 1000), take(1))
        .subscribe(res => resolve(res.result), reject);
    });
  }

  startPing(): void {
    if (this.pingInterval) return;

    const executePing = (): void => {
      this.ping(environment.baseUri).then(() => {
        this.pingChange$.next(true);
        this.flexibleUri = environment.baseUri;
      }).catch(() => {
        this.flexibleUri = environment.backupUri;
        this.pingChange$.next(false);
      });
    };

    executePing();
    this.pingInterval = setInterval(executePing, 15000);
  }

  changeProcessedSession(operationType: OperationType | 'TRANSIT') {
    let color = '';
    switch (operationType) {
      case OperationType.IN:
      case OperationType.OUT:
      case OperationType.NOTICES_ACCEPTED:
      case OperationType.INRETURNED: {
        color = '#06B002';
        break;
      }
      case OperationType.REFUSEIN:
      case OperationType.REFUSEOUT: {
        color = '#B50000';
        break;
      }
      case 'TRANSIT': {
        color = '#4A66CC';
        break;
      }
      default: {
        color = '';
        break;
      }
    }
    const existingSession = localStorage.getItem('processedSession');
    const parsedData = existingSession ? isJsonParsable(existingSession) : null;
    const sessionData: ProcessedSession = parsedData || { total: 0, details: [] };
    const existingOperation = sessionData.details.find((detail) => detail.operation === operationType);
    if (existingOperation) {
      existingOperation.count++;
    } else {
      sessionData.details.push({
        operation: operationType,
        color,
        count: 1
      });
    }
    sessionData.total = sessionData.details.reduce((sum, detail) => sum + detail.count, 0);
    const sessionDataString = JSON.stringify(sessionData);
    localStorage.setItem('processedSession', sessionDataString);
    this.processedSession = sessionData;
    return sessionData;
  }

  addNote(paymentId: string, note: string): Promise<undefined> {
    return new Promise((resolve, reject) => {
      this.http.post<undefined>(environment.baseUri + 'payment/' + paymentId + '/note', { note: note }, this.httpOptions)
        .pipe(timeout(20000)).pipe(take(1)).subscribe(resolve, this.makeReject(reject));
    });
  }

  facialTest(picture: string): Promise<boolean> {
    if (!this.userInfo?.server?.usingFaceDetection) return Promise.resolve(true);
    return new Promise((resolve) => {
      this.http.post<Results<boolean>>(environment.backupUri + 'admin/users/facial_test', { picture: picture }, this.httpOptions)
        .pipe(timeout(300)).pipe(take(1)).subscribe(res => resolve(res.result), () => resolve(true));
    });
  }
}
